<template>
    <div
    class="titles-wrapper"
    :class="{
        'mobile': getMobileAppState,
        'color-background': colorBackground,
        'inside-curtain': insideCurtain,
        'with-big-icon': iconComponent && !insideCurtain,
        'negative-title': negativeTitle,
        'feature-like-header': featureLikeHeader,
        'dark-mode': getAppThemeMode === 'dark'
    }">
        <div class="text-wrapper" :class="{'invert-order': invertTitleSubtitle, 'add-padding-for-icon': !buttonsTopData && iconComponent}">
            <!-- Title -->
            <div class="title" :class="{'helper-text-title': helperTextTitle}" v-mask="titleMask">{{title}}</div>
            <!-- Subtitle -->
            <div v-if="subTitle" class="subtitle"><component :is="subTitleIconComponent"/>{{subTitle}}</div>
        </div>
        <div v-if="buttonsTopData" class="buttons-operations-group-wrapper">
            <!-- Buttons Operations Group -->
            <buttons-operations-group
            :notificationOnlyEmail="buttonsTopData.notificationOnlyEmail"
            :emailAction="buttonsTopData.emailAction"
            :smsAction="buttonsTopData.smsAction"
            :sendPayload="buttonsTopData.sendPayload"
            :downloadUrl="buttonsTopData.downloadUrl"
            :downloadPDFUrl="buttonsTopData.downloadPDFUrl"
            :downloadExcelUrl="buttonsTopData.downloadExcelUrl"
            :operationId="buttonsTopData.operationId"/>
        </div>
        <div v-if="iconComponent" class="big-icon-wrapper"><component :is="iconComponent"/></div>
    </div>
</template>

<script setup>
import ButtonsOperationsGroup from '@/components/generic/buttons/OperationsGroup'
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

const { getMobileAppState, getAppThemeMode } = mapGetters()

/* Props
@invertTitleSubtitle {Boolean} - prop to invert order of Title and Subtitle
@insideCurtain {Boolean} - prop to apply styles for curtain display
@featureLikeHeader {Boolean} - apply styles like a feature wrapper header
@colorBackground {Boolean} - prop to apply styles to make this content visible in colorfull backgrounds
@title {String} - text to display in our title
@titleMask {String} - add mask on our title
@negativeTitle {Boolean} - prop to apply negative color to title
@subTitle {String} - text to display under our title
@subTitleIcon {String} - text to display under our title
@icon {String} - iconName+path(if needed) to display
@buttonsTopData {Object} - object with info needed for ButtonsOperationsGroup
@helperTextTitle {Boolean} - prop to apply helper text title styles
*/
const props = defineProps({
    invertTitleSubtitle: Boolean,
    featureLikeHeader: Boolean,
    insideCurtain: Boolean,
    colorBackground: Boolean,
    title: String,
    titleMask: String,
    negativeTitle: Boolean,
    subTitle: String,
    subTitleIcon: String,
    icon: String,
    buttonsTopData: Object,
    helperTextTitle: Boolean
})

const subTitleIconComponent = computed(() => {
    if (!props.subTitleIcon) return null
    const icon = props.subTitleIcon
    return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})

const iconComponent = computed(() => {
    if (!props.icon) return null
    const icon = props.icon
    return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})
</script>

<style lang="scss" scoped>
.titles-wrapper {
    width: 100%;
    height: auto;

    display: flex;
    flex-direction: column;
    position: relative;

    &.with-big-icon {
        min-height: 80px;
    }

    .text-wrapper {
        display: flex;
        flex-direction: column;

        &.invert-order {
            flex-direction: column-reverse;

            .subtitle {
                margin-top: 0;
            }
        }

        &.add-padding-for-icon {
            .title {
                padding-right: 80px;
            }
        }

        .title {
            width: auto;
            min-height: 28px;

            @include H3-Medium;
            color: var(--primary);
            line-height: 28px;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &.helper-text-title {
                overflow: unset;
                text-overflow: unset;
                white-space: unset;
            }
        }

        .subtitle {
            width: 100%;
            min-height: 20px;
            margin-top: 4px;

            display: flex;

            @include H5-Regular;
            color: var(--secondary-dark-1);

            svg {
                width: 16px;
                height: 16px;

                flex-shrink: 0;

                :deep(.change-color-fill) {
                    fill: var(--secondary-dark-1);
                }
            }
        }
    }

    .buttons-operations-group-wrapper {
        width: auto;
        height: auto;
        margin-top: 24px;

        flex-shrink: 0;
    }

    .big-icon-wrapper {
        width: 80px;
        height: 80px;

        display: flex;
        align-items: center;
        justify-content: center;

        position: absolute;
        right: 0;
        bottom: 0;

        border-radius: 50%;
        background-color: rgba(var(--secondary-light-4-rgb), 0.16);

        svg {
            width: 32px;
            height: 32px;

            flex-shrink: 0;

            :deep(.change-color-fill) {
                fill: var(--secondary-light-4);
            }
        }
    }

    &.color-background {
        .title {
            color: var(--secondary-light-4);
        }

        .subtitle {
            color: var(--secondary-light-4);

            svg {
                :deep(.change-color-fill) {
                    fill: var(--secondary-light-4);
                }
            }
        }
    }

    &.negative-title {
        .text-wrapper {
            .title {
                color: var(--negative);
            }
        }
    }

    &.feature-like-header {
        justify-content: center;
        align-items: center;
        height: 40px;

        &:not(.mobile) {
            padding-left: 24px;
            padding-right: 68px;
        }

        &.mobile {
            padding: 0 62px;
        }

        .text-wrapper {
            .title {
                height: 20px;
                min-height: 20px;

                @include H5-Medium;
                line-height: 20px;
                color: var(--secondary-light-4);
                text-align: center;
            }

            .subtitle {
                height: 13px;

                @include H8-Regular;
                line-height: 13px;
                color: var(--secondary-light-2);
                text-align: center;
            }
        }

        &.dark-mode .text-wrapper {
            .title {
                color: var(--secondary-dark-1);
            }

            .subtitle {
                color: var(--secondary-dark-3);
            }
        }
    }

    &.inside-curtain {
        width: 100%;
        min-height: 48px;
        margin-bottom: 16px;

        justify-content: center;

        &:not(.mobile) {
            padding-left: 24px;
            padding-right: 68px;
        }

        &.mobile {
            padding: 0 62px;
        }

        .text-wrapper.add-padding-for-icon {
            .title {
                padding-right: 0;
            }
        }

        .title {
            width: 100%;
            @include H3-Medium;
            color: var(--secondary-dark-3);
            text-align: center;

            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .subtitle {
            width: 100%;
            @include H5-Regular;
            color: var(--secondary-dark-1);
            text-align: center;

            svg {
                .change-color-fill {
                    fill: var(--secondary-light-4);
                }
            }
        }

        &.mobile {
            min-height: 40px;
            text-align: center;

            .title {
                @include H6-Regular;
            }

            .subtitle {
                @include H8-Regular;
                justify-content: center;

                svg {
                    width: 12px;
                    height: 12px;
                }
            }
        }
    }
}
</style>
